import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { OshIncident } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function OshIncidentDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const oq = useApiQuery<OshIncident>(`/api/v1/m16/osh-incidents/${id}`);
  const isInspectionStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_INSPECTOR, M16_ROLES.CHIEF_INSPECTOR);

  if (oq.loading) return <Spinner label="Loading incident…" />;
  if (oq.error) return <ErrorNote message={oq.error} />;
  if (!oq.data) return <ErrorNote message="Not found" />;
  const incident = oq.data;

  return (
    <div>
      <PageHeader
        title={incident.refNo}
        subtitle={`${incident.classification.replaceAll('_', ' ')} — ${incident.employerEntityId}`}
        actions={<StatusBadge status={incident.investigationStatus} />}
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Incident at</dt>
              <dd className="text-slate-800">{formatDateTime(incident.incidentAt)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Reported at</dt>
              <dd className="text-slate-800">
                {formatDateTime(incident.reportedAt)} — <StatusBadge status={incident.withinStatutoryWindow ? 'OK' : 'OVERDUE'} />
              </dd>
            </div>
          </dl>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Narrative</div>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{incident.narrative}</p>
          </div>

          {incident.injuredParties.length > 0 ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Injured parties</div>
              <ul className="space-y-1.5">
                {incident.injuredParties.map((p, i) => (
                  <li key={i} className="text-sm text-slate-700">
                    <span className="font-semibold">{p.fullName}</span>
                    {p.nin ? <span className="ml-1 font-mono text-xs text-slate-400">({p.nin})</span> : null} — {p.natureOfInjury}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {incident.investigationFindings ? (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Investigation findings</div>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{incident.investigationFindings}</p>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="card p-4 text-sm">
            <h2 className="mb-2 text-xs font-bold uppercase text-slate-500">Investigation</h2>
            <dl className="space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-slate-500">Investigator</dt>
                <dd className="font-mono text-slate-800">{incident.investigatorId ?? '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Closed</dt>
                <dd className="text-slate-800">{incident.closedAt ? formatDateTime(incident.closedAt) : '—'}</dd>
              </div>
            </dl>
          </div>

          {isInspectionStaff && incident.investigationStatus === 'REPORTED' ? (
            <StartInvestigationCard incidentId={incident.id} onDone={oq.reload} />
          ) : null}
          {isInspectionStaff && incident.investigationStatus === 'UNDER_INVESTIGATION' ? (
            <CloseInvestigationCard incidentId={incident.id} onDone={oq.reload} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StartInvestigationCard({ incidentId, onDone }: { incidentId: string; onDone: () => void }) {
  const [investigatorId, setInvestigatorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    if (!investigatorId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/osh-incidents/${incidentId}/investigation`, { investigatorId: investigatorId.trim() });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not start investigation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Start investigation</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <input className="field-input mb-2" placeholder="Investigator ID" value={investigatorId} onChange={(e) => setInvestigatorId(e.target.value)} />
      <button className="btn-primary w-full" onClick={start} disabled={busy || !investigatorId.trim()}>
        {busy ? 'Starting…' : 'Start investigation'}
      </button>
    </div>
  );
}

function CloseInvestigationCard({ incidentId, onDone }: { incidentId: string; onDone: () => void }) {
  const [findings, setFindings] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = async () => {
    if (findings.trim().length < 5) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/osh-incidents/${incidentId}/close`, { findings: findings.trim() });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not close investigation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Close investigation</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea className="field-input mb-2 min-h-[4rem]" placeholder="Findings" value={findings} onChange={(e) => setFindings(e.target.value)} />
      <button className="btn-primary w-full" onClick={close} disabled={busy || findings.trim().length < 5}>
        {busy ? 'Closing…' : 'Close investigation'}
      </button>
    </div>
  );
}
